import React, {useCallback, useMemo} from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import LabelWithTooltip from '@/components/atoms/LabelWithTooltip/LabelWithTooltip';
import PropTypes from 'prop-types';
import './TimePickerInput.scss';

// Matches the DS Dropdown/TextInput clear ("x") icon exactly
const ClearIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M7.99992 1.33334C4.31325 1.33334 1.33325 4.31334 1.33325 8.00001C1.33325 11.6867 4.31325 14.6667 7.99992 14.6667C11.6866 14.6667 14.6666 11.6867 14.6666 8.00001C14.6666 4.31334 11.6866 1.33334 7.99992 1.33334ZM11.3333 10.3933L10.3933 11.3333L7.99992 8.94001L5.60659 11.3333L4.66659 10.3933L7.05992 8.00001L4.66659 5.60668L5.60659 4.66668L7.99992 7.06001L10.3933 4.66668L11.3333 5.60668L8.93992 8.00001L11.3333 10.3933Z'
      fill='var(--color-icon-default)'
    />
  </svg>
);

const TimeCustomInput = React.forwardRef(
  ({value = '', onClick, placeholder, disabled, onClear}, ref) => {
    const handleClearClick = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        onClear?.();
      },
      [onClear]
    );

    return (
      <button
        type='button'
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick?.(e);
        }}
        ref={ref}
        disabled={disabled}
        className={`datepicker-input-btn flex w-full items-center justify-between rounded border text-left ${disabled ? 'datepicker-input-btn--disabled' : ''}`}>
        <span
          className={`datepicker-input-btn__text ${
            value
              ? 'datepicker-input-btn__text--filled'
              : 'datepicker-input-btn__text--placeholder'
          }`}>
          {value || placeholder || 'hh:mm'}
        </span>
        {value && (
          <span
            className='timepicker-clear-icon'
            onClick={handleClearClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClearClick(e);
              }
            }}
            role='button'
            tabIndex={0}
            aria-label='Clear time'>
            <ClearIcon />
          </span>
        )}
      </button>
    );
  }
);

TimeCustomInput.propTypes = {
  value: PropTypes.string,
  onClick: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  onClear: PropTypes.func
};

const parseTimeString = (timeStr) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return null;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const formatTimeToString = (date) => {
  if (!date) return '';
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const TimePickerInput = ({
  label,
  value,
  onChange,
  placeholder = 'hh:mm',
  disabled = false,
  timeIntervals = 15,
  id = '',
  minTime = null,
  maxTime = null,
  showLabelIcon = false,
  tooltipText = ''
}) => {
  const selectedTime = useMemo(() => parseTimeString(value), [value]);
  const minTimeDate = useMemo(() => parseTimeString(minTime), [minTime]);
  const maxTimeDate = useMemo(() => parseTimeString(maxTime), [maxTime]);

  const handleChange = useCallback(
    (date) => {
      onChange(formatTimeToString(date));
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange('');
  }, [onChange]);

  const testId = `time-picker-input${id ? `_${id}` : ''}`;

  return (
    <div className='timepicker-wrapper' data-testid={testId}>
      <div className='timepicker-label-wrapper'>
        <LabelWithTooltip
          className='timepicker-label'
          label={label}
          showLabelIcon={showLabelIcon}
          tooltipText={tooltipText}
          isDark={true}
        />
      </div>
      <DatePicker
        selected={selectedTime}
        onChange={handleChange}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={timeIntervals}
        timeCaption='Time'
        dateFormat='HH:mm'
        timeFormat='HH:mm'
        placeholderText={placeholder}
        disabled={disabled}
        popperClassName='timepicker-popper'
        customInput={
          <TimeCustomInput
            placeholder={placeholder}
            disabled={disabled}
            onClear={handleClear}
          />
        }
        {...(minTimeDate &&
          maxTimeDate && {minTime: minTimeDate, maxTime: maxTimeDate})}
      />
    </div>
  );
};

TimePickerInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  timeIntervals: PropTypes.number,
  id: PropTypes.string,
  minTime: PropTypes.string,
  maxTime: PropTypes.string,
  showLabelIcon: PropTypes.bool,
  tooltipText: PropTypes.string
};

export default React.memo(TimePickerInput);
